import { Component } from "react";
import { reportReactRenderError } from "../../../../../utils/globalErrorHandlers";
import { Box, Tooltip, Typography, IconButton } from "@mui/material";
import { Add, Remove, Link as LinkIcon, Undo } from "@mui/icons-material";
import { useTranslationApp } from "../../../../../lib/i18next";
import {
  ADDED,
  ADS_SCHEDULE_ALWAYS_ON,
  BUTTON_REVERT_CHANGE,
  CUSTOM_PLACEMENTS_PLACEHOLDER,
  CUSTOMER_TYPE_MESSAGE_PLACEHOLDER,
  CUSTOMER_TYPE_PREFILL_MESSAGE,
  CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE,
  GREETING_MESSAGE_PLACEHOLDER,
  LOG_GROUP_AUDIENCE_NETWORK,
  LOG_GROUP_FEEDS,
  LOG_GROUP_REELS_STORIES,
  LOG_MESSAGE_TEMPLATE_CUSTOMER_TEXTS,
  META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE,
  META_ADGROUP_PLACEMENTS_TYPE_MANUAL,
  REVERT_CHANGE_CONFIRM_TITLE,
  REVERT_CHANGE_SUCCESS,
  REVERT_CHANGE_ERROR,
  REMOVED,
} from "../../../../../i18n/keysTranslations";
import {
  getPlacementLabel,
  PLATFORMS,
} from "../../../../../utils/catalogs/metaPlacements";
import { openLink } from "../../../../../utils/string";
import { waitDelay } from "../../../../../utils/date";
import {
  ConfirmAlert,
  SimpleAlert,
} from "../../../../../components/Alerts/Alerts";
import {
  applyReverseLogChange,
  canReverseLogChange,
} from "../utils/applyReverseLogChange";
import {
  ADS_OBJECT_TYPES,
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_ERROR,
  META,
} from "../../../../../utils/constants";
import CardMedia from "../../../../../components/Media/CardMedia";
import ScheduleDisplay from "../../Common/PreviewOptimizationFields/ScheduleDisplay";
import {
  getFieldLabel,
  getFieldKind,
  getDisplayText,
  getLanguageLabel,
  normalizeArrayItems,
  formatValue,
  formatUtmParams,
  formatScheduleSummary,
  formatObjectSummary,
  isScheduleAlwaysOn,
  formatDefaultLogChangeDisplay,
  IMAGE_FIELDS,
  MEDIA_URL_FIELDS,
  SITE_LINK_FIELDS,
  TEXT_LIST_FIELDS,
  CHIPS_ARRAY_FIELDS,
  FIELD_KIND_SCHEDULE,
  FIELD_KIND_OBJECT_SUMMARY,
  FIELD_KIND_CHIPS,
  FIELD_KIND_KEY_VALUE,
  FIELD_KIND_DATE_TIME,
  FIELD_KIND_LINKS,
  FIELD_KIND_MEDIA_PREVIEW,
  FIELD_KIND_ARRAY_ADD_REMOVE,
  FIELD_KIND_LOGO,
  FIELD_KIND_INSIGHT,
} from "../utils/logChangeConfig";
import {
  diffCustomizationPlacements,
  extractCustomizationPlacements,
  groupCustomizationPlacementsByCategory,
  GROUP_AUDIENCE_NETWORK,
  GROUP_FEEDS,
  GROUP_REELS_STORIES,
} from "../utils/customizationPlacementsLogUtils";

class LogChangeItemErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn("[LogChangeItem] Error caught:", error, errorInfo);
    reportReactRenderError({
      error,
      componentStack: errorInfo?.componentStack,
      functionName: "LogChangeItemErrorBoundary",
    });
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const PlacementsDisplay = ({ value, t }) => {
  if (!value || typeof value !== "object") return null;
  const typeKey =
    value.type === "advantage"
      ? META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE
      : META_ADGROUP_PLACEMENTS_TYPE_MANUAL;
  const typeLabel = t ? t(typeKey) : value.type;
  if (value.type === "advantage") {
    return <Typography variant="body2">{typeLabel}</Typography>;
  }
  const byPlatform = {
    facebook: (value.facebookPositions || []).map(getPlacementLabel),
    instagram: (value.instagramPositions || []).map(getPlacementLabel),
    messenger: (value.messengerPositions || []).map(getPlacementLabel),
    audienceNetwork: (value.audienceNetworkPositions || []).map(
      getPlacementLabel
    ),
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="body2" sx={{ mb: 0.25 }}>
        {typeLabel}
      </Typography>
      {PLATFORMS.map((platform) => {
        const labels = byPlatform[platform.key];
        if (!labels?.length) return null;
        return (
          <Box
            key={platform.key}
            sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, ml: 1 }}
          >
            <Typography
              variant="caption"
              component="span"
              sx={{ fontSize: "0.7rem", color: "text.secondary", mr: 0.5 }}
            >
              {platform.label}:
            </Typography>
            <Typography variant="caption" component="span">
              {labels.join(", ")}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const SiteLinkDisplay = ({ siteLink }) => {
  const title = siteLink.linkText || siteLink.title || "";
  const url = siteLink.finalUrl || siteLink.url || "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 1,
        borderRadius: 1,
        mb: 1,
        border: "1px solid rgba(255, 255, 255, 0.12)",
        maxWidth: 300,
        mt: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <LinkIcon fontSize="small" sx={{ mr: 1 }} />
        <Typography variant="body2" component="span" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      {url && (
        <Typography
          variant="body2"
          onClick={() => openLink(url)}
          sx={{ textDecoration: "underline", cursor: "pointer" }}
        >
          {url}
        </Typography>
      )}
      {siteLink.descriptions?.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {siteLink.descriptions.map((desc, i) => (
            <Typography key={i} variant="body2">
              {desc}
            </Typography>
          ))}
        </Box>
      )}
      {siteLink.imageUrl && (
        <Box
          component="img"
          src={siteLink.imageUrl}
          alt=""
          sx={{
            width: 60,
            height: 60,
            objectFit: "cover",
            borderRadius: 1,
            mt: 0.5,
          }}
        />
      )}
    </Box>
  );
};

const ImageDisplay = ({ url }) => (
  <Box
    component="img"
    src={url}
    alt="Image"
    sx={{
      width: 80,
      height: 80,
      objectFit: "cover",
      borderRadius: 1,
      mr: 1,
      mb: 1,
      border: "1px solid rgba(255, 255, 255, 0.12)",
    }}
  />
);

const MediaUrlsDisplay = ({ urls }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    {urls.map((url, index) => (
      <CardMedia
        key={index}
        url={url}
        sx={{ width: 50, height: 50, objectFit: "contain" }}
      />
    ))}
  </Box>
);

const LogoDisplay = ({ url }) => (
  <Box
    component="img"
    src={url}
    alt="Logo"
    sx={{
      width: 60,
      height: 60,
      objectFit: "cover",
      borderRadius: 1,
      mr: 1,
      mb: 1,
      border: "1px solid rgba(255, 255, 255, 0.12)",
    }}
  />
);

const ChipsDisplay = ({ items, field, platform, t }) => {
  const getText = (item) => {
    if (field === "languages" && platform && t) {
      const raw = typeof item === "object" ? (item?.value ?? item?.id) : item;
      return getLanguageLabel(platform, raw, t);
    }
    return getDisplayText(item);
  };
  return (
    <Typography
      variant="body2"
      component="div"
      sx={{
        ml: 3,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {items.map((item, index) => (
        <span key={index}>
          {getText(item)}
          {index < items.length - 1 ? ", " : ""}
        </span>
      ))}
    </Typography>
  );
};

const FieldHeader = ({ label }) => (
  <Typography
    variant="body2"
    sx={{ mb: 0.5, textTransform: "capitalize", fontWeight: "bold" }}
  >
    {label}
  </Typography>
);

const ValueTransition = ({ oldValue, newValue }) => (
  <>
    {!oldValue && oldValue !== 0 ? (
      <Typography variant="body2">{newValue}</Typography>
    ) : (
      <Typography variant="body2">
        {oldValue} → {newValue}
      </Typography>
    )}
  </>
);

const KeyValueDisplay = ({ pairs }) => (
  <Box sx={{ ml: 1, mt: 0.5 }}>
    {pairs.map(({ key, value }, index) => (
      <Typography key={index} variant="body2" sx={{ fontFamily: "monospace" }}>
        {key} = {value}
      </Typography>
    ))}
  </Box>
);

const ObjectSummaryDisplay = ({ value, field, t }) => {
  const summary = formatObjectSummary(value, field, t);
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {summary}
    </Typography>
  );
};

const MESSAGE_TEMPLATE_TYPE_LABELS = {
  preFill: CUSTOMER_TYPE_PREFILL_MESSAGE,
  quickQuestions: CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE,
};

const formatMessageTemplateTexts = (texts) => {
  if (!Array.isArray(texts)) return texts?.[0] ?? "";
  return texts.filter(Boolean).join(", ") || "";
};

const MessageTemplateDisplay = ({ oldValue, newValue, t }) => {
  const oldType = oldValue?.customerTypeMessage;
  const newType = newValue?.customerTypeMessage;
  const typeChanged = oldType !== newType;
  const oldGreeting = oldValue?.greeting ?? "";
  const newGreeting = newValue?.greeting ?? "";
  const greetingChanged = oldGreeting !== newGreeting;
  const oldTexts = formatMessageTemplateTexts(oldValue?.customerTexts);
  const newTexts = formatMessageTemplateTexts(newValue?.customerTexts);
  const textsChanged = oldTexts !== newTexts;

  if (!typeChanged && !greetingChanged && !textsChanged) return null;

  const getTypeLabel = (type) => {
    if (!type) return "-";
    const key = MESSAGE_TEMPLATE_TYPE_LABELS[type];
    return key ? t(key) : type;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {typeChanged && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {t(CUSTOMER_TYPE_MESSAGE_PLACEHOLDER)}
          </Typography>
          <Typography variant="body2">
            {getTypeLabel(oldType)} → {getTypeLabel(newType)}
          </Typography>
        </Box>
      )}
      {greetingChanged && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {t(GREETING_MESSAGE_PLACEHOLDER)}
          </Typography>
          <ValueTransition
            oldValue={oldGreeting || "-"}
            newValue={newGreeting || "-"}
          />
        </Box>
      )}
      {textsChanged && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {t(LOG_MESSAGE_TEMPLATE_CUSTOMER_TEXTS)}
          </Typography>
          <ValueTransition
            oldValue={oldTexts || "-"}
            newValue={newTexts || "-"}
          />
        </Box>
      )}
    </Box>
  );
};

const LinkDisplay = ({ value }) => {
  const url = typeof value === "string" ? value : "";
  if (!url) return null;
  return (
    <Typography
      variant="body2"
      onClick={() => openLink(url)}
      sx={{
        textDecoration: "underline",
        cursor: "pointer",
        wordBreak: "break-all",
      }}
    >
      {url}
    </Typography>
  );
};

const isChangeReversible = (change) => {
  if (change.type === "create") return false;
  if (
    change.field === "insight" ||
    change.objectType === ADS_OBJECT_TYPES.insight
  )
    return false;
  const hasOldValue = Object.prototype.hasOwnProperty.call(change, "oldValue");
  const hasArrayChange =
    (change.added && change.added.length > 0) ||
    (change.removed && change.removed.length > 0);
  return hasOldValue || hasArrayChange;
};

const LogChangeItem = ({
  change,
  isLast,
  objectType,
  platform,
  enableReverse = false,
  reverseContext = null,
}) => {
  const { t } = useTranslationApp();
  const fieldLabel = getFieldLabel({ field: change.field, t });
  const fieldKind = getFieldKind(change.field);

  const canReverseBase =
    enableReverse &&
    reverseContext &&
    typeof reverseContext.getValues === "function" &&
    typeof reverseContext.setValue === "function" &&
    isChangeReversible(change);

  const canReverse =
    canReverseBase &&
    canReverseLogChange({
      change,
      objectType: objectType || change.objectType,
      platform,
      getValues: reverseContext.getValues,
    });

  const onReverseClick = async () => {
    const result = await ConfirmAlert({
      title: t(REVERT_CHANGE_CONFIRM_TITLE),
      confirmButtonText: t(BUTTON_REVERT_CHANGE),
    });
    if (!result?.isConfirmed) return;
    const applyResult = applyReverseLogChange({
      change,
      objectType: objectType || change.objectType,
      platform,
      getValues: reverseContext.getValues,
      setValue: reverseContext.setValue,
    });
    if (applyResult.ok) {
      SimpleAlert({
        title: t(REVERT_CHANGE_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });

      if (
        typeof reverseContext.onChangeSectionSelected === "function" &&
        applyResult.ids
      ) {
        reverseContext.onChangeSectionSelected({
          platform: applyResult.ids.platform,
          adGroupID: applyResult.ids.adGroupID,
          adID: applyResult.ids.adID,
          assetsGroupID: applyResult.ids.assetsGroupID,
        });
      }
      await waitDelay(1000);
      if (applyResult.path) {
        const fieldInDom = document.getElementById(applyResult.path);
        if (fieldInDom) {
          fieldInDom.scrollIntoView({ behavior: "smooth" });
          if (typeof fieldInDom.focus === "function") {
            fieldInDom.focus();
          }
        }
      }
    } else {
      console.log("applyResult", applyResult);
      SimpleAlert({
        title: t(REVERT_CHANGE_ERROR),
        icon: ALERT_ICON_TYPE_ERROR,
      });
    }
  };

  const renderArrayAddRemove = (items, isAdded) => {
    if (!items || items.length === 0) return null;
    const normalized = normalizeArrayItems(items);

    return (
      <>
        <Typography
          variant="body2"
          color={isAdded ? "#66bb6a" : "#ef5350"}
          sx={{
            display: "flex",
            alignItems: "center",
            mt: isAdded ? 0 : 1,
          }}
        >
          {isAdded ? (
            <Add fontSize="small" sx={{ mr: 0.5 }} />
          ) : (
            <Remove fontSize="small" sx={{ mr: 0.5 }} />
          )}
          {isAdded ? t(ADDED) : t(REMOVED)}
        </Typography>

        {IMAGE_FIELDS.includes(change.field) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", mt: 0.5 }}>
            {normalized.map((url, index) => (
              <ImageDisplay key={index} url={url} />
            ))}
          </Box>
        )}

        {MEDIA_URL_FIELDS.includes(change.field) && (
          <MediaUrlsDisplay urls={normalized} />
        )}

        {SITE_LINK_FIELDS.includes(change.field) &&
          normalized.map((link, index) => (
            <SiteLinkDisplay key={index} siteLink={link} />
          ))}

        {TEXT_LIST_FIELDS.includes(change.field) && (
          <Box sx={{ ml: 3 }}>
            {normalized.map((item, index) => (
              <Typography key={index} variant="body2">
                {getDisplayText(item)}
              </Typography>
            ))}
          </Box>
        )}

        {CHIPS_ARRAY_FIELDS.includes(change.field) && (
          <ChipsDisplay
            items={normalized}
            field={change.field}
            platform={platform}
            t={t}
          />
        )}
      </>
    );
  };

  const renderChangeContent = () => {
    if (fieldKind === FIELD_KIND_INSIGHT) {
      return (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#E1E1E1",
              borderRadius: 1,
              pl: 1,
              pr: 1,
              py: 0.5,
              display: "inline-block",
            }}
          >
            <Typography variant="body2">
              {change?.newValue?.title || ""}
            </Typography>
          </Box>
          <Tooltip title={change?.newValue?.action || ""}>
            <Typography
              variant="caption"
              sx={{ mb: 0.5, whiteSpace: "normal" }}
            >
              {change?.newValue?.action || ""}
            </Typography>
          </Tooltip>
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_ARRAY_ADD_REMOVE) {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {change?.added && renderArrayAddRemove(change.added, true)}
          {change?.removed && renderArrayAddRemove(change.removed, false)}
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_LOGO) {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {!change.oldValue ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LogoDisplay url={change.newValue} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LogoDisplay url={change.oldValue} />
              <Typography variant="body2" sx={{ mx: 1 }}>
                →
              </Typography>
              <LogoDisplay url={change.newValue} />
            </Box>
          )}
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_SCHEDULE) {
      const oldSchedule = formatScheduleSummary(change.oldValue);
      const newSchedule = formatScheduleSummary(change.newValue);
      const oldIsAlwaysOn = isScheduleAlwaysOn(change.oldValue);
      const newIsAlwaysOn = isScheduleAlwaysOn(change.newValue);
      const alwaysOnLabel = t(ADS_SCHEDULE_ALWAYS_ON);
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {(oldSchedule || oldIsAlwaysOn) && (
              <Box sx={{ display: "block" }}>
                {oldSchedule ? (
                  <ScheduleDisplay scheduleValue={oldSchedule} emptyValue="-" />
                ) : (
                  <Typography variant="body2">{alwaysOnLabel}</Typography>
                )}
              </Box>
            )}
            {(oldSchedule || oldIsAlwaysOn) &&
              (newSchedule || newIsAlwaysOn) && (
                <Typography variant="body2" sx={{ my: 0.5, display: "block" }}>
                  →
                </Typography>
              )}
            {(newSchedule || newIsAlwaysOn) && (
              <Box sx={{ display: "block" }}>
                {newSchedule ? (
                  <ScheduleDisplay scheduleValue={newSchedule} emptyValue="-" />
                ) : (
                  <Typography variant="body2">{alwaysOnLabel}</Typography>
                )}
              </Box>
            )}
          </Box>
          {!oldSchedule && !newSchedule && !oldIsAlwaysOn && !newIsAlwaysOn && (
            <Typography variant="body2">-</Typography>
          )}
        </Box>
      );
    }

    if (change.field === "messageTemplate") {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          <MessageTemplateDisplay
            oldValue={change.oldValue}
            newValue={change.newValue}
            t={t}
          />
        </Box>
      );
    }

    if (change.field === "placements") {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {change.oldValue && (
            <Box sx={{ mb: 0.5 }}>
              <PlacementsDisplay value={change.oldValue} t={t} />
              <Typography variant="body2" sx={{ my: 0.25 }}>
                →
              </Typography>
            </Box>
          )}
          <PlacementsDisplay value={change.newValue} t={t} />
        </Box>
      );
    }

    if (change.field === "biddingStrategy") {
      const formattedOld = formatObjectSummary(
        change.oldValue,
        change.field,
        t
      );
      const formattedNew = formatObjectSummary(
        change.newValue,
        change.field,
        t
      );
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          <ValueTransition oldValue={formattedOld} newValue={formattedNew} />
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_OBJECT_SUMMARY) {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {change.oldValue && (
            <Box sx={{ mb: 0.5 }}>
              <ObjectSummaryDisplay
                value={change.oldValue}
                field={change.field}
                t={t}
              />
              <Typography variant="body2" sx={{ my: 0.25 }}>
                →
              </Typography>
            </Box>
          )}
          <ObjectSummaryDisplay
            value={change.newValue}
            field={change.field}
            t={t}
          />
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_CHIPS) {
      if (change.added || change.removed) {
        return (
          <Box sx={{ mt: 1 }}>
            <FieldHeader label={fieldLabel} />
            {change.added && renderChipsAddRemove(change.added, true)}
            {change.removed && renderChipsAddRemove(change.removed, false)}
          </Box>
        );
      }
      const items = Array.isArray(change.newValue)
        ? change.newValue
        : [change.newValue];
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          <ChipsDisplay
            items={items}
            field={change.field}
            platform={platform}
            t={t}
          />
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_KEY_VALUE) {
      const oldPairs = formatUtmParams(change.oldValue);
      const newPairs = formatUtmParams(change.newValue);
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {oldPairs.length > 0 && (
            <>
              <KeyValueDisplay pairs={oldPairs} />
              <Typography variant="body2" sx={{ my: 0.25 }}>
                →
              </Typography>
            </>
          )}
          {newPairs.length > 0 ? (
            <KeyValueDisplay pairs={newPairs} />
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_DATE_TIME) {
      const formattedOld = formatValue(change.oldValue, change.field, t);
      const formattedNew = formatValue(change.newValue, change.field, t);
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          <ValueTransition oldValue={formattedOld} newValue={formattedNew} />
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_LINKS) {
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {change.oldValue && (
            <>
              <LinkDisplay value={change.oldValue} />
              <Typography variant="body2" sx={{ my: 0.25 }}>
                →
              </Typography>
            </>
          )}
          <LinkDisplay value={change.newValue} />
        </Box>
      );
    }

    if (fieldKind === FIELD_KIND_MEDIA_PREVIEW) {
      const oldPlacements = extractCustomizationPlacements(change.oldValue);
      const newPlacements = extractCustomizationPlacements(change.newValue);
      const hasCustomizationPlacements =
        platform === META &&
        (oldPlacements.length > 0 || newPlacements.length > 0);

      if (hasCustomizationPlacements) {
        const diff = diffCustomizationPlacements(oldPlacements, newPlacements);
        const groupToLabel = {
          [GROUP_FEEDS]: LOG_GROUP_FEEDS,
          [GROUP_REELS_STORIES]: LOG_GROUP_REELS_STORIES,
          [GROUP_AUDIENCE_NETWORK]: LOG_GROUP_AUDIENCE_NETWORK,
        };

        const getMediaUrl = (item) =>
          item?.mediaUrl || item?.preview || item?.url || "";

        const oldByGroup =
          groupCustomizationPlacementsByCategory(oldPlacements);
        const newByGroup =
          groupCustomizationPlacementsByCategory(newPlacements);

        return (
          <Box sx={{ mt: 1 }}>
            <FieldHeader label={t(CUSTOM_PLACEMENTS_PLACEHOLDER)} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {diff.removed.map((group) => {
                const item = oldByGroup[group];
                const url = item ? getMediaUrl(item) : "";
                return (
                  <Box
                    key={`removed-${group}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Remove fontSize="small" sx={{ color: "#ef5350" }} />
                    <Typography variant="body2" color="#ef5350">
                      {t(REMOVED)}: {t(groupToLabel[group] || group)}
                    </Typography>
                    {url && (
                      <CardMedia
                        url={url}
                        sx={{ width: 40, height: 40, objectFit: "contain" }}
                      />
                    )}
                  </Box>
                );
              })}
              {diff.added.map((group) => {
                const item = newByGroup[group];
                const url = item ? getMediaUrl(item) : "";
                return (
                  <Box
                    key={`added-${group}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Add fontSize="small" sx={{ color: "#66bb6a" }} />
                    <Typography variant="body2" color="#66bb6a">
                      {t(ADDED)}: {t(groupToLabel[group] || group)}
                    </Typography>
                    {url && (
                      <CardMedia
                        url={url}
                        sx={{ width: 40, height: 40, objectFit: "contain" }}
                      />
                    )}
                  </Box>
                );
              })}
              {diff.changed.map(({ group, oldItem, newItem }) => {
                const oldUrl = getMediaUrl(oldItem);
                const newUrl = getMediaUrl(newItem);
                return (
                  <Box
                    key={`changed-${group}`}
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t(groupToLabel[group] || group)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {oldUrl && (
                        <CardMedia
                          url={oldUrl}
                          sx={{ width: 50, height: 50, objectFit: "contain" }}
                        />
                      )}
                      <Typography variant="body2">→</Typography>
                      {newUrl && (
                        <CardMedia
                          url={newUrl}
                          sx={{ width: 50, height: 50, objectFit: "contain" }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
              {diff.removed.length === 0 &&
                diff.added.length === 0 &&
                diff.changed.length === 0 && (
                  <Typography variant="body2">-</Typography>
                )}
            </Box>
          </Box>
        );
      }

      const urls = Array.isArray(change.newValue)
        ? change.newValue
            .map((item) => item?.preview || item?.url || item)
            .filter((u) => typeof u === "string")
        : [];
      return (
        <Box sx={{ mt: 1 }}>
          <FieldHeader label={fieldLabel} />
          {urls.length > 0 ? (
            <MediaUrlsDisplay urls={urls} />
          ) : (
            <Typography variant="body2">
              {Array.isArray(change.newValue)
                ? `${change.newValue.length} media item(s)`
                : "-"}
            </Typography>
          )}
        </Box>
      );
    }

    const formattedOldValue = formatDefaultLogChangeDisplay(
      change.oldValue,
      change.field,
      t
    );
    const formattedNewValue = formatDefaultLogChangeDisplay(
      change.newValue,
      change.field,
      t
    );

    return (
      <Box sx={{ mt: 1 }}>
        <FieldHeader label={fieldLabel} />
        <ValueTransition
          oldValue={formattedOldValue}
          newValue={formattedNewValue}
        />
      </Box>
    );
  };

  const renderChipsAddRemove = (items, isAdded) => {
    if (!items || items.length === 0) return null;
    return (
      <>
        <Typography
          variant="body2"
          color={isAdded ? "#66bb6a" : "#ef5350"}
          sx={{
            display: "flex",
            alignItems: "center",
            mt: isAdded ? 0 : 1,
          }}
        >
          {isAdded ? (
            <Add fontSize="small" sx={{ mr: 0.5 }} />
          ) : (
            <Remove fontSize="small" sx={{ mr: 0.5 }} />
          )}
          {isAdded ? t(ADDED) : t(REMOVED)}
        </Typography>
        <ChipsDisplay
          items={normalizeArrayItems(items)}
          field={change.field}
          platform={platform}
          t={t}
        />
      </>
    );
  };

  if (change.type === "create") return null;

  return (
    <Box
      sx={{
        mb: isLast ? 0 : 2,
        mt: 1,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{renderChangeContent()}</Box>
      {canReverse && (
        <Tooltip title={t(BUTTON_REVERT_CHANGE)}>
          <IconButton
            size="small"
            onClick={onReverseClick}
            sx={{ flexShrink: 0 }}
          >
            <Undo fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const LogChangeItemWithBoundary = (props) => (
  <LogChangeItemErrorBoundary>
    <LogChangeItem {...props} />
  </LogChangeItemErrorBoundary>
);

export default LogChangeItemWithBoundary;
